import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { getArraysIntersection } from 'helpers/Utils';
import { rolesConstants } from 'constants/Roles';
import Spinner from 'components/Core/Spinner/Spinner';

/**
 * Safely parse user from localStorage with error handling
 * If localStorage contains malformed JSON, returns null and clears the corrupt data
 */
const getUserFromStorage = () => {
  try {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  } catch (e) {
    localStorage.removeItem('user');
    return null;
  }
};

// Timeout in milliseconds before redirecting if roles don't load
// 8 seconds balances tolerance for poor networks with reasonable UX
const ROLES_LOADING_TIMEOUT = 8000;

/**
 * Component to handle the loading state with timeout
 */
const RolesLoadingFallback = ({ location, onRolesLoaded }) => {
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasTimedOut(true);
    }, ROLES_LOADING_TIMEOUT);

    // Check periodically if roles have loaded
    // 1000ms interval balances responsiveness with performance
    const checkInterval = setInterval(() => {
      const user = getUserFromStorage();
      if (user && user.roles) {
        // Clean up before triggering re-render to prevent memory leaks
        clearTimeout(timer);
        clearInterval(checkInterval);
        // Roles loaded, trigger re-render using React state
        onRolesLoaded();
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(checkInterval);
    };
  }, [onRolesLoaded]);

  if (hasTimedOut) {
    // Clear potentially corrupted user data and redirect to login
    localStorage.removeItem('user');
    return <Redirect to={{ pathname: '/', state: { from: location } }} />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '16px',
      }}
    >
      <Spinner size={50} />
      <p style={{ color: '#666', fontSize: '14px' }}>Chargement des informations utilisateur...</p>
    </div>
  );
};

RolesLoadingFallback.propTypes = {
  location: PropTypes.object.isRequired,
  onRolesLoaded: PropTypes.func.isRequired,
};

const PrivateRoute = ({ component: Component, allowedRoles, ...rest }) => {
  // State to trigger re-render when roles are loaded
  const [rolesLoadedTrigger, setRolesLoadedTrigger] = useState(0);

  // Callback to trigger re-render when roles are loaded
  // Wrapped in useCallback to prevent interval reset on every render
  const handleRolesLoaded = useCallback(() => {
    setRolesLoadedTrigger((prev) => prev + 1);
  }, []);

  const handleRender = (props) => {
    const localStorageUser = getUserFromStorage();

    // If no user in localStorage, redirect to login
    if (!localStorageUser) {
      return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
    }

    // If no roles required for this route, allow access
    if (allowedRoles === null) {
      return <Component {...props} />;
    }

    // If roles not yet loaded, show loading state with timeout fallback
    // This prevents unauthorized access during the loading window
    if (!localStorageUser.roles) {
      return <RolesLoadingFallback location={props.location} onRolesLoaded={handleRolesLoaded} />;
    }

    // Check if user has required roles
    if (
      localStorageUser.roles.includes(rolesConstants.ROLE_SUPER_ADMIN) ||
      getArraysIntersection(allowedRoles, localStorageUser.roles).length
    ) {
      return <Component {...props} />;
    }

    return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
  };

  return (
    <Route
      {...rest}
      render={(props) =>
        localStorage.getItem('user') ? (
          handleRender(props)
        ) : (
          <Redirect to={{ pathname: '/', state: { from: props.location } }} />
        )
      }
    />
  );
};

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
};

PrivateRoute.defaultProps = {
  allowedRoles: null,
};

export default PrivateRoute;
